import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { I18nProvider, LOCALES, type Locale } from "./i18n";
import { AuthProvider } from "./auth";
import { FavoritesProvider } from "./favorites";
import { AppShell } from "./layout/AppShell";
import {
  AttractionDetailPage,
  AttractionsPage,
  DestinationDetailPage,
  DestinationsPage,
  EventDetailPage,
  EventsPage,
  ExperienceDetailPage,
  ExperiencesPage,
  HomePage,
  HotelDetailPage,
  HotelsPage,
  LoginPage,
  NearbyPage,
  PlanTripPage,
  RegisterPage,
  RestaurantDetailPage,
  RestaurantsPage,
  SearchPage,
  TravelGuideDetailPage,
  TravelGuidePage,
} from "./pages";

const SavedPage = lazy(() =>
  import("./pages/SavedPage").then((m) => ({ default: m.SavedPage })),
);
const ProfilePage = lazy(() =>
  import("./pages/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const AdminCmsPage = lazy(() =>
  import("./pages/AdminCmsPage").then((m) => ({ default: m.AdminCmsPage })),
);
const ExplorePage = lazy(() =>
  import("./pages/ExplorePage").then((m) => ({ default: m.ExplorePage })),
);
const MapPage = lazy(() =>
  import("./pages/MapPage").then((m) => ({ default: m.MapPage })),
);

function RouteFallback() {
  return <p className="py-20 text-center text-ridge">Loading…</p>;
}

function LocaleLayout() {
  const { locale } = useParams();
  const safe = (LOCALES.includes(locale as Locale) ? locale : "en") as Locale;
  if (locale && !LOCALES.includes(locale as Locale)) {
    return <Navigate to="/en" replace />;
  }
  return (
    <I18nProvider locale={safe}>
      <AuthProvider>
        <FavoritesProvider>
          <AppShell>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route index element={<HomePage />} />
                <Route path="explore" element={<ExplorePage />} />
                <Route path="map" element={<MapPage />} />
                <Route path="destinations" element={<DestinationsPage />} />
                <Route path="destinations/:slug" element={<DestinationDetailPage />} />
                <Route path="attractions" element={<AttractionsPage />} />
                <Route path="attractions/:slug" element={<AttractionDetailPage />} />
                <Route path="hotels" element={<HotelsPage />} />
                <Route path="hotels/:slug" element={<HotelDetailPage />} />
                <Route path="restaurants" element={<RestaurantsPage />} />
                <Route path="restaurants/:slug" element={<RestaurantDetailPage />} />
                <Route path="experiences" element={<ExperiencesPage />} />
                <Route path="experiences/:slug" element={<ExperienceDetailPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="events/:slug" element={<EventDetailPage />} />
                <Route path="travel-guide" element={<TravelGuidePage />} />
                <Route path="travel-guide/:slug" element={<TravelGuideDetailPage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="plan-trip" element={<PlanTripPage />} />
                <Route path="nearby" element={<NearbyPage />} />
                <Route path="saved" element={<SavedPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="admin" element={<AdminCmsPage />} />
              </Routes>
            </Suspense>
          </AppShell>
        </FavoritesProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/en" replace />} />
      <Route path="/:locale/*" element={<LocaleLayout />} />
      <Route path="*" element={<Navigate to="/en" replace />} />
    </Routes>
  );
}
